import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Paper,
  Stack,
  Grid,
  IconButton,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Menu
} from "@mui/material";
import {
  SaveAlt,
  CalendarToday,
  Circle
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { isArray } from "lodash";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import { getBackendUrl } from "../../config";
import api from "../../services/api";

const backendUrl = getBackendUrl();

const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// CustomAttendantsTable
// ---------------------------------------------------------------------------
const CustomAttendantsTable = ({ attendants, loading }) => {
  const themeV5 = useThemeV5();

  const getAvatarUrl = (attendant) => {
    const imageUrl = attendant.profileImage;
    if (imageUrl && imageUrl.trim() !== "") {
      return `${backendUrl}/public/avatar/${imageUrl}`;
    }
    return null;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  };

  const imgErrors = useRef({});
  const [, forceUpdate] = useState(0);

  const handleAvatarError = (id) => {
    if (!imgErrors.current[id]) {
      imgErrors.current[id] = true;
      forceUpdate(n => n + 1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!attendants || attendants.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="body1" color="text.secondary">
          {i18n.t("dashboard.noAttendants") || "Nenhum atendente encontrado"}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Atendente
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Avaliação
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              NPS
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Avaliados
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Total Atend.
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Tempo Médio
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px" }}>
              Perfil
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attendants.map((attendant, index) => {
            const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
            const avatarColor = colors[index % colors.length];
            const avatarSrc = getAvatarUrl(attendant);
            const attendantId = attendant.id || index;
            const effectiveSrc = (avatarSrc && !imgErrors.current[attendantId]) ? avatarSrc : null;

            return (
              <TableRow
                key={attendant.id || index}
                sx={{
                  "&:hover": {
                    bgcolor: "action.hover",
                    transition: "background-color 0.2s"
                  }
                }}
              >
                <TableCell>
                  <Chip
                    icon={
                      <Circle
                        sx={{
                          fontSize: 8,
                          animation: attendant.online ? "pulse 2s ease-in-out infinite" : "none"
                        }}
                      />
                    }
                    label={attendant.online ? "Online" : "Offline"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      bgcolor: attendant.online ? "rgba(16, 185, 129, 0.08)" : "rgba(148, 163, 184, 0.08)",
                      color: attendant.online ? "#10b981" : "text.secondary",
                      border: "1px solid",
                      borderColor: attendant.online ? "rgba(16, 185, 129, 0.2)" : "rgba(148, 163, 184, 0.2)",
                      "& .MuiChip-icon": {
                        color: attendant.online ? "#10b981" : "text.secondary"
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={effectiveSrc}
                      alt={attendant.name}
                      onError={() => handleAvatarError(attendantId)}
                      sx={{
                        width: 36,
                        height: 36,
                        border: `1.5px solid ${themeV5.palette.divider}`,
                        bgcolor: !effectiveSrc ? avatarColor : undefined,
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: !effectiveSrc ? "#fff" : undefined
                      }}
                    >
                      {!effectiveSrc && getInitials(attendant.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "text.primary", fontSize: "0.875rem" }}>
                        {attendant.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                        {attendant.email || "Sem email"}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <svg width="14" height="14" fill="#f59e0b" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                      {attendant.rating?.toFixed(1) || "0.0"}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  {attendant.ratingsCount > 0 ? (
                    <Chip
                      label={attendant.nps || 0}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.813rem",
                        minWidth: 50,
                        bgcolor: 
                          (attendant.nps >= 75) ? "rgba(16, 185, 129, 0.1)" :
                          (attendant.nps >= 50) ? "rgba(5, 150, 105, 0.1)" :
                          (attendant.nps >= 0) ? "rgba(245, 158, 11, 0.1)" :
                          "rgba(239, 68, 68, 0.1)",
                        color: 
                          (attendant.nps >= 75) ? "#10b981" :
                          (attendant.nps >= 50) ? "#059669" :
                          (attendant.nps >= 0) ? "#f59e0b" :
                          "#ef4444",
                        border: "1px solid",
                        borderColor: 
                          (attendant.nps >= 75) ? "rgba(16, 185, 129, 0.2)" :
                          (attendant.nps >= 50) ? "rgba(5, 150, 105, 0.2)" :
                          (attendant.nps >= 0) ? "rgba(245, 158, 11, 0.2)" :
                          "rgba(239, 68, 68, 0.2)"
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                    {attendant.ratingsCount || 0}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                    {attendant.attendances || 0}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                    {attendant.avgSupportTime ? 
                      (typeof attendant.avgSupportTime === 'number' ? 
                        `${Math.floor(attendant.avgSupportTime)}:${String(Math.round((attendant.avgSupportTime % 1) * 60)).padStart(2, '0')}` 
                        : attendant.avgSupportTime) 
                      : "0:00"}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={attendant.profile === 'admin' ? 'Administrador' : attendant.profile === 'user' ? 'Usuário' : attendant.profile || "Usuário"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      bgcolor: attendant.profile === 'admin' ? "rgba(59, 130, 246, 0.1)" : "rgba(148, 163, 184, 0.1)",
                      color: attendant.profile === 'admin' ? "#3b82f6" : "text.secondary",
                      border: "1px solid",
                      borderColor: attendant.profile === 'admin' ? "rgba(59, 130, 246, 0.2)" : "rgba(148, 163, 184, 0.2)"
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------
const StatCard = ({ title, value, icon, color, isLive }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        height: "100%",
        minHeight: 140,
        borderRadius: 3,
        background: "#fff",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isHovered ? "translateY(-2px)" : "translateY(0)",
        cursor: "pointer",
        position: "relative",
        overflow: "visible",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          borderColor: color
        }
      }}
    >
      <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.7rem"
                }}
              >
                {title}
              </Typography>
              {isLive && (
                <Chip
                  icon={<Circle sx={{ fontSize: 6, animation: "pulse 2s ease-in-out infinite" }} />}
                  label="Ao vivo"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    bgcolor: "rgba(16, 185, 129, 0.08)",
                    color: "#10b981",
                    border: "1px solid rgba(16, 185, 129, 0.2)",
                    "& .MuiChip-icon": { color: "#10b981" }
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                lineHeight: 1.2,
                wordBreak: "break-word"
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              opacity: 0.9
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// DateRangePicker
// ---------------------------------------------------------------------------
const DateRangePicker = ({ dateRange, onDateRangeChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingStartDate, setSelectingStartDate] = useState(true);

  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);

  const formatDateDisplay = (date) => {
    const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const handleClick = (event) => { setAnchorEl(event.currentTarget); };
  const handleClose = () => { setAnchorEl(null); };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const handleDateClick = (day) => {
    const selectedDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    if (selectingStartDate) {
      setStartDate(selectedDate);
      if (selectedDate > endDate) setEndDate(selectedDate);
      setSelectingStartDate(false);
    } else {
      setEndDate(selectedDate);
      if (selectedDate < startDate) setStartDate(selectedDate);
      setSelectingStartDate(true);
    }
  };

  const isDateInRange = (day) => {
    const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    return date >= startDate && date <= endDate;
  };

  const isSameDay = (day, compareDate) => {
    const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    return date.getDate() === compareDate.getDate() &&
           date.getMonth() === compareDate.getMonth() &&
           date.getFullYear() === compareDate.getFullYear();
  };

  const handlePrevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };
  
  const handleNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const handleApplyDates = () => {
    if (onDateRangeChange) onDateRangeChange({ startDate, endDate });
    handleClose();
  };

  const handleClearDates = () => {
    const newStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const newEndDate = new Date();
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCalendarMonth(new Date());
    if (onDateRangeChange) onDateRangeChange({ startDate: newStartDate, endDate: newEndDate });
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(calendarMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={<CalendarToday sx={{ fontSize: 18 }} />}
        sx={{
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 3,
          py: 1.25,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          textTransform: "none",
          fontWeight: 600,
          color: "text.primary",
          fontSize: "0.875rem",
          "&:hover": { 
            bgcolor: "action.hover",
            borderColor: "primary.main"
          }
        }}
      >
        {formatDateDisplay(startDate)} - {formatDateDisplay(endDate)}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            mt: 1,
            minWidth: 380,
            maxWidth: 420,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            p: 3,
            border: "1px solid",
            borderColor: "divider"
          }
        }}
      >
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", mb: 1, display: "block", color: "text.secondary", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                Data Início
              </Typography>
              <Button
                fullWidth
                onClick={() => setSelectingStartDate(true)}
                sx={{
                  py: 1.5,
                  justifyContent: "flex-start",
                  borderRadius: 2,
                  bgcolor: selectingStartDate ? "primary.main" : "action.hover",
                  color: selectingStartDate ? "primary.contrastText" : "text.primary",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": { bgcolor: selectingStartDate ? "primary.dark" : "action.selected" }
                }}
              >
                {formatDateDisplay(startDate)}
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", pb: 1 }}>
              <Typography variant="h6" sx={{ color: "text.secondary" }}>→</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", mb: 1, display: "block", color: "text.secondary", fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                Data Fim
              </Typography>
              <Button
                fullWidth
                onClick={() => setSelectingStartDate(false)}
                sx={{
                  py: 1.5,
                  justifyContent: "flex-start",
                  borderRadius: 2,
                  bgcolor: !selectingStartDate ? "primary.main" : "action.hover",
                  color: !selectingStartDate ? "primary.contrastText" : "text.primary",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": { bgcolor: !selectingStartDate ? "primary.dark" : "action.selected" }
                }}
              >
                {formatDateDisplay(endDate)}
              </Button>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <IconButton onClick={handlePrevMonth} size="small">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </IconButton>
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: "0.875rem" }}>
              {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
            </Typography>
            <IconButton onClick={handleNextMonth} size="small">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </IconButton>
          </Stack>

          <Grid container spacing={0.5} sx={{ mb: 1 }}>
            {dayNames.map((day) => (
              <Grid item xs={12/7} key={day}>
                <Typography variant="caption" align="center" display="block" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.7rem" }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={0.5} sx={{ mb: 3 }}>
            {Array.from({ length: startingDayOfWeek }).map((_, index) => (
              <Grid item xs={12/7} key={`empty-${index}`}>
                <Box sx={{ height: 36 }} />
              </Grid>
            ))}
            {days.map((day) => {
              const isStart = isSameDay(day, startDate);
              const isEnd = isSameDay(day, endDate);
              const inRange = isDateInRange(day);
              return (
                <Grid item xs={12/7} key={day}>
                  <Button
                    onClick={() => handleDateClick(day)}
                    sx={{
                      minWidth: 36,
                      height: 36,
                      p: 0,
                      borderRadius: 1.5,
                      fontSize: "0.813rem",
                      fontWeight: (isStart || isEnd) ? 700 : 500,
                      bgcolor: (isStart || isEnd) ? "primary.main" : inRange ? "rgba(25, 118, 210, 0.08)" : "transparent",
                      color: (isStart || isEnd) ? "primary.contrastText" : inRange ? "primary.main" : "text.primary",
                      "&:hover": { bgcolor: (isStart || isEnd) ? "primary.dark" : "action.hover" }
                    }}
                  >
                    {day}
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyDates}
              sx={{ 
                py: 1.25, 
                borderRadius: 2, 
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                textTransform: "none"
              }}
            >
              Aplicar Filtro
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearDates}
              sx={{ 
                py: 1.25, 
                borderRadius: 2, 
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none"
              }}
            >
              Limpar
            </Button>
          </Stack>
        </Box>
      </Menu>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
const Dashboard = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);
  const hasLoaded = useRef(false);

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const data = await find(params);
      setCounters(data.counters);
      
      const originalAttendants = isArray(data.attendants) ? data.attendants : [];
      let attendantsWithImages = originalAttendants;
      
      try {
        const usersResponse = await api.get('/users');
        const users = usersResponse.data.users || [];
        attendantsWithImages = originalAttendants.map(att => {
          const user = users.find(u => u.id === att.id);
          return user ? { ...att, profileImage: user.profileImage, email: user.email, companyId: user.companyId } : att;
        });
      } catch (e) {
        console.error("Erro ao buscar imagens dos usuários:", e);
      }
      
      setAttendants(attendantsWithImages);
    } catch (error) {
      toast.error("Não foi possível carregar os dados do dashboard.");
      console.error(error);
    }
    setLoading(false);
    hasLoaded.current = true;
  };

  useEffect(() => {
    const params = {
      date_from: dateFilter.startDate.toISOString().slice(0, 10),
      date_to: dateFilter.endDate.toISOString().slice(0, 10)
    };
    fetchData(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const exportToExcel = () => {
    try {
      const exportData = attendants.map(attendant => ({
        'Status': attendant.online ? 'Online' : 'Offline',
        'Nome': attendant.name,
        'Email': attendant.email || '',
        'Pontuação': attendant.rating?.toFixed(1) || '0.0',
        'NPS': attendant.nps || 0,
        'Avaliados': attendant.ratingsCount || 0,
        'Total Atendimentos': attendant.attendances || 0,
        'Tempo Médio': attendant.avgSupportTime || '0:00',
        'Perfil': attendant.profile || 'user'
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendentes");
      XLSX.writeFile(wb, "relatorio-de-atendentes.xlsx");
      toast.success("Relatório exportado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao exportar para Excel.");
    }
  };

  const getOnlineUsersCount = () => attendants.filter(u => u.online).length;

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const statCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="10" fill={PRIMARY_MAIN} opacity="0.1"/>
          <path d="M14 18C14 16.34 15.34 15 17 15H31C32.66 15 34 16.34 34 18V28C34 29.66 32.66 31 31 31H25L19 35V31H17C15.34 31 14 29.66 14 28V18Z" stroke={PRIMARY_MAIN} strokeWidth="2" strokeLinejoin="round" fill="none"/>
          <circle cx="21" cy="24" r="1.5" fill={PRIMARY_MAIN}/>
          <circle cx="24" cy="24" r="1.5" fill={PRIMARY_MAIN}/>
          <circle cx="27" cy="24" r="1.5" fill={PRIMARY_MAIN}/>
        </svg>
      ),
      color: PRIMARY_MAIN,
      isLive: true
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="10" fill="#f59e0b" opacity="0.1"/>
          <circle cx="24" cy="24" r="10" stroke="#f59e0b" strokeWidth="2" fill="none"/>
          <path d="M24 17V24L29 27" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      color: "#f59e0b"
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="10" fill="#10b981" opacity="0.1"/>
          <circle cx="24" cy="24" r="10" stroke="#10b981" strokeWidth="2" fill="none"/>
          <path d="M18 24L22 28L30 20" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      color: "#10b981"
    },
    {
      title: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="10" fill="#8b5cf6" opacity="0.1"/>
          <circle cx="19" cy="20" r="4" stroke="#8b5cf6" strokeWidth="2" fill="none"/>
          <circle cx="29" cy="19" r="3" stroke="#8b5cf6" strokeWidth="2" fill="none"/>
          <path d="M12 32C12 28.69 15.13 26 19 26C22.87 26 26 28.69 26 32" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" fill="none"/>
          <path d="M29 25C31.21 25 33 26.79 33 29" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" fill="none"/>
        </svg>
      ),
      color: "#8b5cf6"
    },
    {
      title: i18n.t("dashboard.cards.activeAttendants"),
      value: `${getOnlineUsersCount()}/${attendants.length}`,
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="10" fill="#06b6d4" opacity="0.1"/>
          <circle cx="24" cy="19" r="5" stroke="#06b6d4" strokeWidth="2" fill="none"/>
          <path d="M14 33C14 28.03 18.03 24 23 24H25C29.97 24 34 28.03 34 33" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" fill="none"/>
          <circle cx="32" cy="16" r="2.5" fill="#06b6d4"/>
        </svg>
      ),
      color: "#06b6d4"
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: counters.leads || 0,
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="10" fill="#f43f5e" opacity="0.1"/>
          <circle cx="22" cy="20" r="5" stroke="#f43f5e" strokeWidth="2" fill="none"/>
          <path d="M13 33C13 28.03 17.03 24 22 24C26.97 24 31 28.03 31 33" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" fill="none"/>
          <path d="M32 18V26" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round"/>
          <path d="M28 22H36" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      color: "#f43f5e"
    }
  ];

  // CÁLCULO CORRETO DO NPS E SATISFAÇÃO
  const calculateMetrics = () => {
    // Percentuais já vêm calculados do backend
    const promoters = counters.npsPromotersPerc || 0;  // % de notas 9-10
    const detractors = counters.npsDetractorsPerc || 0; // % de notas 0-6
    const passives = counters.npsPassivePerc || 0;     // % de notas 7-8
    
    // NPS = % Promotores - % Detratores (já calculado no backend)
    // Exemplo: 83% promotores - 0% detratores = NPS 83
    // Exemplo: 50% promotores - 30% detratores = NPS 20
    const npsScore = counters.npsScore || 0;
    
    // Satisfação: média das notas (0-10) - agora vem do backend como avgSatisfaction
    const satisfactionScore = counters.avgSatisfaction || 0;
    
    return {
      nps: npsScore,
      satisfaction: satisfactionScore,
      promoters,
      passives,
      detractors,
      totalTickets: counters.tickets || 0,
      withRating: counters.withRating || 0,
      percRating: counters.percRating || 0
    };
  };

  const metrics = calculateMetrics();

  // Função para determinar classificação do NPS
  const getNPSClassification = (score) => {
    if (score >= 75) return { label: 'Excelente', color: '#10b981' };
    if (score >= 50) return { label: 'Muito Bom', color: '#059669' };
    if (score >= 0) return { label: 'Razoável', color: '#f59e0b' };
    if (score >= -50) return { label: 'Ruim', color: '#f97316' };
    return { label: 'Crítico', color: '#ef4444' };
  };

  const npsClassification = getNPSClassification(metrics.nps);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("dashboard.title") || "Dashboard"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Visão geral de desempenho
              </Typography>
            </Box>
          </Stack>

          <DateRangePicker dateRange={dateFilter} onDateRangeChange={setDateFilter} />
        </Stack>

        {/* Metrics Cards */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {statCards.map((card, index) => (
            <Grid item xs={6} sm={6} md={4} lg={2} key={index}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* Attendants Table */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {i18n.t("dashboard.tabs.attendants") || "Atendentes"}
              </Typography>
              <Button
                onClick={exportToExcel}
                startIcon={<SaveAlt />}
                variant="contained"
                sx={{
                  bgcolor: PRIMARY_MAIN,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  "&:hover": {
                    bgcolor: PRIMARY_DARK,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                  }
                }}
              >
                Exportar
              </Button>
            </Stack>
          </Box>
          <Box sx={{ p: 3 }}>
            <CustomAttendantsTable attendants={attendants} loading={loading} />
          </Box>
        </Paper>

        {/* Métricas de Avaliação - NPS e Satisfação */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Card NPS */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                overflow: "hidden",
                height: "100%"
              }}
            >
              <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                  Net Promoter Score (NPS)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  % Promotores - % Detratores
                </Typography>
              </Box>
              
              <Box sx={{ p: 4 }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box sx={{ display: "inline-flex", position: "relative", mb: 2 }}>
                    <svg width="180" height="180">
                      <circle cx="90" cy="90" r="75" fill="none" stroke="#e5e7eb" strokeWidth="14" />
                      <circle 
                        cx="90" 
                        cy="90" 
                        r="75" 
                        fill="none" 
                        stroke={npsClassification.color}
                        strokeWidth="14" 
                        strokeLinecap="round" 
                        strokeDasharray="471" 
                        strokeDashoffset={471 - (471 * (metrics.nps + 100) / 200)} 
                        transform="rotate(-90 90 90)"
                        style={{ transition: "stroke-dashoffset 1s ease-out" }}
                      />
                    </svg>
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <Typography variant="h1" fontWeight={700} sx={{ fontSize: "3rem", lineHeight: 1, color: npsClassification.color }}>
                        {metrics.nps}
                      </Typography>
                      <Typography variant="caption" sx={{ color: npsClassification.color, fontWeight: 600, mt: 1, fontSize: "0.875rem" }}>
                        {npsClassification.label}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mt: 2 }}>
                    Score NPS do Período
                  </Typography>
                  <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Escala de Classificação NPS
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                      <Chip label="75-100: Excelente" size="small" sx={{ bgcolor: "rgba(16, 185, 129, 0.1)", color: "#10b981", fontSize: "0.7rem" }} />
                      <Chip label="50-74: Muito Bom" size="small" sx={{ bgcolor: "rgba(5, 150, 105, 0.1)", color: "#059669", fontSize: "0.7rem" }} />
                      <Chip label="0-49: Razoável" size="small" sx={{ bgcolor: "rgba(245, 158, 11, 0.1)", color: "#f59e0b", fontSize: "0.7rem" }} />
                      <Chip label="< 0: Crítico" size="small" sx={{ bgcolor: "rgba(239, 68, 68, 0.1)", color: "#ef4444", fontSize: "0.7rem" }} />
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Card Satisfação */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                overflow: "hidden",
                height: "100%"
              }}
            >
              <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                  Índice de Satisfação
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Média geral das avaliações (0-10)
                </Typography>
              </Box>
              
              <Box sx={{ p: 4 }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box sx={{ display: "inline-flex", position: "relative", mb: 2 }}>
                    <svg width="180" height="180">
                      <circle cx="90" cy="90" r="75" fill="none" stroke="#e5e7eb" strokeWidth="14" />
                      <circle 
                        cx="90" 
                        cy="90" 
                        r="75" 
                        fill="none" 
                        stroke="#3b82f6"
                        strokeWidth="14" 
                        strokeLinecap="round" 
                        strokeDasharray="471" 
                        strokeDashoffset={471 - (471 * metrics.satisfaction / 10)} 
                        transform="rotate(-90 90 90)"
                        style={{ transition: "stroke-dashoffset 1s ease-out" }}
                      />
                    </svg>
                    <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <Typography variant="h1" fontWeight={700} sx={{ fontSize: "3rem", lineHeight: 1, color: "#3b82f6" }}>
                        {metrics.satisfaction.toFixed(1)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#3b82f6", fontWeight: 600, mt: 1, fontSize: "0.875rem" }}>
                        de 10.0
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mt: 2 }}>
                    Nota Média de Satisfação
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} width="24" height="24" fill={i < Math.round(metrics.satisfaction / 2) ? "#f59e0b" : "#e5e7eb"} viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Distribuição Detalhada */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Distribuição de Avaliações
            </Typography>
          </Box>
          
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Distribution */}
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  {/* Promoters */}
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "rgba(16, 185, 129, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="18" height="18" fill="none" stroke="#10b981" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                            Promotores
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            Notas 9-10
                          </Typography>
                        </Box>
                      </Stack>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="h6" fontWeight={700} color="#10b981" sx={{ fontSize: "1.25rem" }}>
                          {Math.round((metrics.promoters / 100) * metrics.withRating)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          {metrics.promoters.toFixed(1)}% dos avaliados
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ height: 8, bgcolor: "#f3f4f6", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${metrics.promoters}%`, bgcolor: "#10b981", borderRadius: 1, transition: "width 1s ease-out" }} />
                    </Box>
                  </Box>

                  {/* Neutrals */}
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "rgba(245, 158, 11, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="18" height="18" fill="none" stroke="#f59e0b" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                            Neutros
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            Notas 7-8
                          </Typography>
                        </Box>
                      </Stack>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="h6" fontWeight={700} color="#f59e0b" sx={{ fontSize: "1.25rem" }}>
                          {Math.round((metrics.passives / 100) * metrics.withRating)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          {metrics.passives.toFixed(1)}% dos avaliados
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ height: 8, bgcolor: "#f3f4f6", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${metrics.passives}%`, bgcolor: "#f59e0b", borderRadius: 1, transition: "width 1s ease-out" }} />
                    </Box>
                  </Box>

                  {/* Detractors */}
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="18" height="18" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                            Detratores
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            Notas 0-6
                          </Typography>
                        </Box>
                      </Stack>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="h6" fontWeight={700} color="#ef4444" sx={{ fontSize: "1.25rem" }}>
                          {Math.round((metrics.detractors / 100) * metrics.withRating)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          {metrics.detractors.toFixed(1)}% dos avaliados
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ height: 8, bgcolor: "#f3f4f6", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${metrics.detractors}%`, bgcolor: "#ef4444", borderRadius: 1, transition: "width 1s ease-out" }} />
                    </Box>
                  </Box>
                </Stack>
              </Grid>

              {/* Summary Stats */}
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  <Box sx={{ p: 3, bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", mb: 1.5, fontSize: "0.7rem" }}>
                      Total de Atendimentos
                    </Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ fontSize: "2rem", mb: 0.5 }}>
                      {metrics.totalTickets.toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ p: 3, bgcolor: "rgba(59, 130, 246, 0.05)", borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", mb: 1.5, fontSize: "0.7rem" }}>
                      Atendimentos Avaliados
                    </Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ fontSize: "2rem", mb: 0.5, color: "#3b82f6" }}>
                      {metrics.withRating.toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ p: 3, bgcolor: "rgba(16, 185, 129, 0.05)", borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", mb: 1.5, fontSize: "0.7rem" }}>
                      Taxa de Avaliação
                    </Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ fontSize: "2rem", color: "#10b981" }}>
                      {metrics.percRating.toFixed(1)}%
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;